import * as React from 'react';
import { Box, IconButton, List, ListItem, ListItemText, Typography, alpha } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone';

export type FileDropzoneProps = {
    value: File[];
    onChange: (files: File[]) => void;
    /** Cap on held files. Omitted means no limit. At 1, a new file replaces the held one. */
    maxFiles?: number;
    /** react-dropzone Accept map. Omitted means any file type. */
    accept?: Accept;
    /** Per-file size cap in bytes. Omitted means no limit. */
    maxSize?: number;
    disabled?: boolean;
    label?: string;
    /** Show a caption restating the accept / size / count restrictions enforced here. */
    showHint?: boolean;
};

/** Build the restriction caption from the same props the dropzone enforces, so they can't drift. */
export const buildHint = (accept?: Accept, maxSize?: number, maxFiles?: number): string => {
    const parts: string[] = [];
    if (accept) {
        const exts = [...new Set(Object.values(accept).flat())].map(e =>
            e.replace('.', '').toUpperCase()
        );
        if (exts.length) parts.push(exts.join(', '));
    }
    if (maxSize != null) parts.push(`max ${+(maxSize / 1048576).toFixed(1)} MiB each`);
    if (maxFiles != null) parts.push(`${maxFiles} file${maxFiles === 1 ? '' : 's'} max`);
    return parts.join(' · ');
};

export const FileDropzone = ({
    value,
    onChange,
    maxFiles,
    accept,
    maxSize,
    disabled,
    label = 'Drop files here, or click to select',
    showHint = false,
}: FileDropzoneProps) => {
    const [error, setError] = React.useState<string | null>(null);
    const limitMsg = `You can add up to ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`;

    const onDrop = (accepted: File[], rejected: FileRejection[]) => {
        // With multiple off, react-dropzone rejects every file of a 2+ file drop as too-many-files.
        const messages = [
            ...new Set(
                rejected.map(r =>
                    r.errors.some(e => e.code === 'too-many-files')
                        ? limitMsg
                        : r.errors.some(e => e.code === 'file-too-large')
                          ? `${r.file.name}: exceeds the size limit.`
                          : `${r.file.name}: ${r.errors[0].message}`
                )
            ),
        ];
        const next = maxFiles === 1 ? accepted.slice(0, 1) : [...value, ...accepted];
        if (maxFiles != null && next.length > maxFiles) {
            // Whole drop rejected; value stays unchanged.
            setError([...new Set([limitMsg, ...messages])].join(' '));
            return;
        }
        if (accepted.length) onChange(next);
        setError(messages.length ? messages.join(' ') : null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept,
        maxSize,
        disabled,
        // Single-select native picker at maxFiles 1; the cap itself is enforced in onDrop.
        multiple: maxFiles !== 1,
        useFsAccessApi: false,
        onDrop,
    });

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 3,
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    bgcolor: theme =>
                        isDragActive
                            ? alpha(theme.palette.primary.main, 0.08)
                            : theme.palette.action.hover,
                    transition: theme =>
                        theme.transitions.create(['border-color', 'background-color']),
                    '&:hover': disabled
                        ? undefined
                        : {
                              borderColor: 'primary.main',
                              bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
                          },
                }}
            >
                <input {...getInputProps()} />
                <CloudUploadOutlinedIcon
                    color={isDragActive ? 'primary' : 'action'}
                    sx={{ fontSize: 32 }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
                {showHint && (
                    <Typography variant="caption" color="text.secondary">
                        {buildHint(accept, maxSize, maxFiles)}
                    </Typography>
                )}
            </Box>
            {error && (
                <Typography role="alert" variant="caption" color="error" component="p">
                    {error}
                </Typography>
            )}
            {/* Kept outside the root so a remove click does not open the picker. */}
            {value.length > 0 && (
                <List dense>
                    {value.map((f, i) => (
                        <ListItem
                            key={`${f.name}-${i}`}
                            secondaryAction={
                                <IconButton
                                    size="small"
                                    aria-label={`Remove ${f.name}`}
                                    disabled={disabled}
                                    onClick={() => {
                                        onChange(value.filter((_, j) => j !== i));
                                        setError(null);
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            }
                        >
                            <ListItemText
                                primary={f.name}
                                secondary={`${Math.round(f.size / 1024)} KB`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

FileDropzone.displayName = 'FileDropzone';
